import { QueryClient, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { AppError, toAppError, type RpcArgs } from '@mt/contracts';
import { rpc } from './api.ts';

export function createQueryClient(onUnauthenticated: () => void): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          const e = toAppError(error);
          if (e.code === 'NETWORK_ERROR' || e.code === 'BACKEND_UNAVAILABLE') return failureCount < 3;
          return false;
        },
        retryDelay: (attempt) => Math.min(8000, 800 * 2 ** attempt),
      },
      mutations: { retry: false },
    },
  });
  const handle = (error: unknown) => {
    if (toAppError(error).code === 'NOT_AUTHENTICATED') onUnauthenticated();
  };
  client.getQueryCache().config.onError = handle;
  client.getMutationCache().config.onError = handle;
  return client;
}

export const keys = {
  me: ['me'] as const,
  home: ['home'] as const,
  path: (clef: string) => ['path', clef] as const,
  skills: ['skills'] as const,
  history: ['history'] as const,
  rewards: ['rewards'] as const,
  classes: ['classes'] as const,
  leaderboard: (classroomId: string) => ['leaderboard', classroomId] as const,
  globalLeaderboard: ['globalLeaderboard'] as const,
  session: (id: string) => ['session', id] as const,
  teacherDashboard: ['teacher', 'dashboard'] as const,
  classroom: (id: string) => ['teacher', 'classroom', id] as const,
  plan: (id: string) => ['teacher', 'plan', id] as const,
  templates: ['teacher', 'templates'] as const,
  assignmentReport: (id: string) => ['teacher', 'report', 'assignment', id] as const,
  studentReport: (classroomId: string, studentId: string) => ['teacher', 'report', 'student', classroomId, studentId] as const,
};

export const useMe = (enabled = true) => useQuery({ queryKey: keys.me, queryFn: () => rpc('get_me'), enabled });
export const useStudentHome = () => useQuery({ queryKey: keys.home, queryFn: () => rpc('get_student_home'), refetchInterval: 60_000 });
export const usePathProgress = (clef: string | undefined) =>
  useQuery({ queryKey: keys.path(clef ?? ''), queryFn: () => rpc('get_path_progress', { p_clef: clef! }), enabled: Boolean(clef) });
export const useSkillProgress = () => useQuery({ queryKey: keys.skills, queryFn: () => rpc('get_skill_progress') });
export const useHistory = () => useQuery({ queryKey: keys.history, queryFn: () => rpc('get_activity_history', { p_limit: 50, p_before: null }) });
export const useRewards = () => useQuery({ queryKey: keys.rewards, queryFn: () => rpc('get_rewards') });
export const useMyClasses = () => useQuery({ queryKey: keys.classes, queryFn: () => rpc('get_my_classes') });
export const useLeaderboard = (classroomId: string) =>
  useQuery({ queryKey: keys.leaderboard(classroomId), queryFn: () => rpc('get_leaderboard', { p_classroom_id: classroomId }), refetchInterval: 60_000 });
export const useGlobalLeaderboard = () =>
  useQuery({ queryKey: keys.globalLeaderboard, queryFn: () => rpc('get_global_leaderboard', { p_period: 'all-time', p_limit: 50 }), refetchInterval: 60_000 });
export const useTeacherDashboard = () => useQuery({ queryKey: keys.teacherDashboard, queryFn: () => rpc('get_teacher_dashboard') });
export const useClassroom = (id: string) => useQuery({ queryKey: keys.classroom(id), queryFn: () => rpc('get_classroom', { p_classroom_id: id }) });
export const usePlan = (id: string) => useQuery({ queryKey: keys.plan(id), queryFn: () => rpc('get_plan', { p_plan_id: id }) });
export const useTemplates = () => useQuery({ queryKey: keys.templates, queryFn: () => rpc('list_plan_templates') });
export const useAssignmentReport = (id: string) =>
  useQuery({ queryKey: keys.assignmentReport(id), queryFn: () => rpc('get_assignment_report', { p_assignment_id: id }) });
export const useStudentReport = (classroomId: string, studentId: string) =>
  useQuery({
    queryKey: keys.studentReport(classroomId, studentId),
    queryFn: () => rpc('get_student_report', { p_classroom_id: classroomId, p_student_id: studentId }),
  });

/** Mutation helper that invalidates the given keys on success. */
export function useRpcMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>, invalidate: (vars: TVars, result: TResult) => QueryKey[] = () => []) {
  const queryClient = useQueryClient();
  return useMutation<TResult, AppError, TVars>({
    mutationFn: async (vars) => {
      try {
        return await fn(vars);
      } catch (error) {
        throw toAppError(error);
      }
    },
    onSuccess: async (result, vars) => {
      await Promise.all(invalidate(vars, result).map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
}

export type ProfilePatch = RpcArgs['update_profile']['p_patch'];
